import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppService } from '../../../core/services/app.service';
import { EventService } from '../../../core/services/event.service';
import * as moment from 'moment';
import { Lightbox } from 'ngx-lightbox';
import { PhotoOrder } from '../../../shared/models/cameralla-photo-order.model';
import { GlobalVariables } from '../../../shared/models/cameralla-global.model';
import { UserService } from '../../user/services/user.service';
import { CamerallaAuthService } from '../../../shared/services/authentication-services/cameralla-auth.service';
import { CamerallaUser } from '../../../shared/models/cameralla-user.model';
import { from } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class PhotoService extends EventService {
    static SET_PHOTOS_FILTER = 'set-filter';
    static LAZY_LOADING_PHOTOS = 'lazy-loading-photo';
    static SEARCH = 'photo-search';
    photoUrl = AppService.getApiUrl() + '/api/photos';
    photoUrlOrder = AppService.getApiUrl() + '/api/photos/filter_default';
    photoIdsByCriteria = AppService.getApiUrl() + '/api/photos/photoIdsByCriteria';
    cachedPhotos: any;
    modifiedPhoto: any
    userProfile: CamerallaUser;
    static VIP_FEEDS_MODE = false;
    static CURRENT_TIME_START = {};
    static CURRENT_TIME_GROUP_START = {};
    userId;
    static GOLDEN_PHOTO_IDS: any = [];

    constructor(protected http: HttpClient,
                protected lightbox: Lightbox,
                protected userService: UserService,
                protected authService: CamerallaAuthService) {
        super();
        this.userId = +this.authService.getLocalUserInfo()['id'];
    }

    getPhotosByOrder(filterList: any[], photoOrder: PhotoOrder, limit, skip, userId = null, defaultFilter?, searchText?, selectFeed?, globalFeed?, kartraTagIds?, isVip = false, isMyFollowing = false) {
        if (skip == 0 && photoOrder.selectedPartner.id) { //get the latest time
            PhotoService.CURRENT_TIME_START[photoOrder.selectedPartner.id] = moment();
        }

        let partnerId = [];
        let cameraType = [];
        const hasStarOrder = photoOrder.selectedBrowse.value === 'starsCount';

        if (photoOrder.selectedPartner.id === GlobalVariables.partnerObjects.CANON.id) {
            selectFeed = GlobalVariables.sourceObjects.SYSTEM.id;
        }

        // Show all photos related to partner.
        if (globalFeed) {
            partnerId.push({partnerId: photoOrder.selectedPartner.id});
        }

        // Show all photos with the "vinter" and "#vinter" tags with Hurtigruten members
        if (defaultFilter && defaultFilter.tags && photoOrder.selectedPartner.id === GlobalVariables.partnerObjects.HURTIGRUTEN.id) {
            if (defaultFilter.tags.length > 0 && defaultFilter.tags.includes('vinter', '#vinter')) {
                partnerId = [];
            }
        }
        
        if (selectFeed) {
            if (selectFeed === GlobalVariables.sourceObjects.SYSTEM.id) {
                cameraType = [{cameraType: selectFeed}, {cameraType: null}];
            } else {
                if (selectFeed !== 99) {
                    cameraType = [{cameraType: selectFeed}];
                }
            }
        } else {
            cameraType = [];
        }

        // Not show private photo and photoPartnerID == 99 - explore page only
        if (!partnerId.length || isVip) {
            partnerId = [{partnerId: {neq: 0}}, {partnerId: {neq: 99}}];
        }


        let queryObject: any = {
            where: {
                and: [
                    ...partnerId,
                    {or: cameraType}
                ]
            }
            ,
            userId: userId,
            include: 'customer',
            vip: isVip,
            followingOfCustomerId: isMyFollowing ? this.userId: false,
            order: hasStarOrder ? `general_count ${photoOrder.selectedOrder.value}` : `${photoOrder.selectedBrowse.value} ${photoOrder.selectedOrder.value}`,
            skip: +skip,
            limit: +limit,
            isStarType: hasStarOrder,
            searchText: searchText,
        };

        if (selectFeed === 99) {
            queryObject['where']['and'].push({
                isFirstPhoto: true
            });
        }

        if (defaultFilter && defaultFilter.tags) {
            if (defaultFilter.tags.length == 1) {
                queryObject['where']['and'].push({
                    tags: {
                        like: `%${defaultFilter.tags[0]}%`
                    }
                });
            }

            if (defaultFilter.tags.length > 1) {
                queryObject['where']['and'].push({
                    or: []
                });

                defaultFilter.tags.forEach((tag) => {
                    queryObject['where']['and'][queryObject['where']['and'].length - 1].or.push({
                            tags: {
                                like: `%${tag}%`
                            }
                        }
                    );
                });
            }
        } else { // not show photo with tags 'feedback1', '#feedback1' on feeds without default filter
            ['feedback1', '#feedback1'].forEach((tag) => {
                queryObject['where']['and'].push({
                    tags: {
                        nlike: `%${tag}%`
                    }
                });
            });
        }

        queryObject['where']['and'].push({
            groupId: null
        });

        //filter golden camera
        if (defaultFilter && defaultFilter.goldenPhotoFilter) {
            queryObject['where']['and'].push({
                isGolden: {
                    neq: 0
                }
            });

            queryObject['where']['and'].push({
                isGolden: {
                    neq: null
                }
            });
            
            return this.getGoldenPhotos(queryObject);
        }

        //filter golden location
        if (defaultFilter && defaultFilter.goldenLocationFilter) {
            queryObject['where']['and'].push({
                isGoldenLocation: 1
            });

            const timeParams = {
                create_at: {
                    between: defaultFilter.period
                }
            };

            queryObject['where']['and'].push(timeParams);
        }

        //filter golden location
        if (defaultFilter && defaultFilter.editorChoiceFilter) {
            queryObject['where']['and'].push({
                isEditorChoice: 1
            });
        }


        queryObject['where']['and'].push({
            create_at: {
                lt: PhotoService.CURRENT_TIME_START[photoOrder.selectedPartner.id]
            }
        });
        queryObject = this.processParamsByOrder(queryObject, photoOrder, filterList);

        const filter = encodeURIComponent(JSON.stringify(queryObject));
        const url = `${this.photoUrlOrder}?filter=${filter}`;

        return this.http.get(url);
    }

    getUserPhotos(myId, selectedBrowses: { name: string, value: string }, typeOfTime: string, limit, skip) {
        let queryObject = {
            where: {
                and: [
                    {customerId: +myId},
                    {
                        groupId: null
                    }
                ],
            },
            include: 'customer',
            order: 'viewsCount DESC',
            skip: +skip,
            limit: +limit
        };

        queryObject = this.processParams(queryObject, selectedBrowses, {name: 'Descending', value: 'DESC'}, {
            name: 'Total Avg.',
            value: 'general_count'
        }, typeOfTime);
        const filter = encodeURIComponent(JSON.stringify(queryObject));
        const url = `${this.photoUrl}?filter=${filter}`;

        return this.http.get(url);
    }

    getPhotosById(ids, limit = 100, skip = 0, groupId = null) {
        let queryObject: Record<string, any> = {
            where: {
                id : {
                    inq : ids
                }
            },
            include: 'customer',
            order: 'viewsCount DESC',
            skip: +skip,
            limit: +limit
        };
        
        if (groupId) {
            queryObject.where.groupId = groupId;
        }


        const filter = encodeURIComponent(JSON.stringify(queryObject));
        const url = `${this.photoUrl}?filter=${filter}`;

        return this.http.get(url);
    }

    getPhotosByLocationId(locationId, limit = 50, skip = 0) {
        let queryObject = {
            where: {
                locationId : locationId,
                isPrimaryLocation: false
            },
            include: 'customer',
            order: 'create_at DESC',
            skip: +skip,
            limit: +limit
        };


        const filter = encodeURIComponent(JSON.stringify(queryObject));
        const url = `${this.photoUrl}?filter=${filter}`;

        return this.http.get(url);
    }

    getUserPhotosByObject(myId, photoOrder: PhotoOrder, limit, skip, isUserView = false, groupId = null, filterList = [], followingMode = false) {
        myId = +myId

        if (groupId && skip == 0) {
            PhotoService.CURRENT_TIME_GROUP_START[groupId] = moment();
        }
        
        
        let queryObject: any = {
            where: {
                and: [
                    {
                        groupId: groupId
                    }
                ],
            },
            include: 'customer',
            order: 'viewsCount DESC',
            skip: +skip,
            limit: +limit
        };

        if (!groupId) {
            if (!followingMode) {
                queryObject.where.and.push({customerId: +myId});
            } else {
                queryObject.followingOfCustomerId = +myId
            }
        } else {
            queryObject.where.and.push({
                create_at: {
                    lt: PhotoService.CURRENT_TIME_GROUP_START[groupId]
                }
            })
            
            if (myId) {
                queryObject.where.and.push({customerId: +myId});
            }
        }

        if (isUserView) {
            queryObject.where.and.push({
                partnerId: {neq: 0}
            });
        }

        queryObject = this.processParams(queryObject, photoOrder && photoOrder.selectedBrowse ? photoOrder.selectedBrowse : {
                name: 'Most Recent',
                value: 'create_at'
            },
            photoOrder && photoOrder.selectedOrder ? photoOrder.selectedOrder : {
                name: 'Descending',
                value: 'DESC'
            }, {name: 'Total Avg.', value: 'general_count'},
            photoOrder && photoOrder.selectedTimeFrame && photoOrder.selectedTimeFrame.value ? photoOrder.selectedTimeFrame.value : 'allTime', filterList);
        const filter = encodeURIComponent(JSON.stringify(queryObject));
        const url = `${this.photoUrl}?filter=${filter}`;

        return this.http.get(url);
    }

    processParams(queryObject, selectedBrowses: { name: string, value: string }, selectedOrders: { name: string, value: string }, selectedRatings: { name: string, value: string }, typeOfTime: string, filterList?: any[]) {
        // If there is any selected category----------------------
        if (filterList && filterList.length) {
            queryObject['where']['and'].push(this.processCategory(filterList));
        }
        // If there is any sorting option selected-----------------
        if (selectedBrowses.value) {
            queryObject['order'] = selectedBrowses.value === 'starsCount' ?
                `${selectedRatings.value} ${selectedOrders.value}` :
                `${selectedBrowses.value} ${selectedOrders.value}`;
        }
        // If there is any time frame----------------------
        if (typeOfTime && typeOfTime !== 'allTime') {
            const time = this.processTimeFrame(typeOfTime); // time.from - time.to
            const timeParams = {
                create_at: {
                    between: [
                        `${time.from}`,
                        `${time.to}`
                    ]
                }
            };

            queryObject['where']['and'].push(timeParams);
        }

        return queryObject;
    }

    processParamsByOrder(queryObject, photoOrder: PhotoOrder, filterList?: any) {
        // If there is any selected category----------------------
        if (filterList && filterList.length) {
            queryObject['where']['and'].push(this.processCategory(filterList));
        }
        // If there is any sorting option selected-----------------
        if (photoOrder.selectedBrowse.value) {
            queryObject['order'] = photoOrder.selectedBrowse.value === 'starsCount' ?
                `general_count ${photoOrder.selectedOrder.value}` :
                `${photoOrder.selectedBrowse.value} ${photoOrder.selectedOrder.value}`;
        }
        // If there is any time frame----------------------
        if (photoOrder.selectedTimeFrame && photoOrder.selectedTimeFrame.value !== 'allTime') {
            const time = this.processTimeFrame(photoOrder.selectedTimeFrame.value); // time.from - time.to
            const timeParams = {
                create_at: {
                    between: [
                        `${time.from}`,
                        `${time.to}`
                    ]
                }
            };

            queryObject['where']['and'].push(timeParams);
        }

        return queryObject;
    }

    processCategory(filterList) {
        const categoryQuery = {
            or: []
        }
        const filter_list = {
            categoryId: {
                inq: []
            }
        };
        for (const each of filterList) {
            if (each.subCategories && each.subCategories.length) {
                categoryQuery['or'].push({
                    categoryId: each.value,
                    subCategoryId: {
                        inq: each.subCategories
                    }
                });
            } else {
                filter_list.categoryId.inq.push(each.value);
            }
        }

        categoryQuery['or'].push(filter_list);

        return categoryQuery;
    }

    processTimeFrame(type) {
        const now = new Date();
        let from;
        let to;
        switch (type) {
            case 'today':
                from = this.toYearMonthDate(moment(now));
                to = this.toYearMonthDate(moment(now).add(1, 'days'));

                return {
                    from: from,
                    to: to,
                };
            case 'week':
                const weekStart = moment().clone().startOf('isoWeek');
                // from = this.toYearMonthDate(moment(now).subtract(6, 'days'));
                // to = this.toYearMonthDate(moment(now).add(1, 'days'));

                from = moment(weekStart).add(0, 'days').format('YYYY-MM-DD');
                to = moment(weekStart).add(6, 'days').format('YYYY-MM-DD');

                return {
                    from: from,
                    to: to,
                };
            case 'last24Hours':
                to = moment().utc().format('YYYY-MM-DD HH:mm:ss')
                from = moment().subtract(24, 'hours').utc().format('YYYY-MM-DD HH:mm:ss')

                return {
                    from: from,
                    to: to,
                };
            case 'last7Days':
                to = moment().utc().format('YYYY-MM-DD HH:mm:ss')
                from = moment().subtract(7, 'days').utc().format('YYYY-MM-DD HH:mm:ss')

                return {
                    from: from,
                    to: to,
                };
            case 'last30Days':
                to = moment().utc().format('YYYY-MM-DD HH:mm:ss')
                from = moment().subtract(30, 'days').utc().format('YYYY-MM-DD HH:mm:ss')

                return {
                    from: from,
                    to: to,
                };
            case'month':
                const monthStart = moment([now.getFullYear(), now.getMonth(), 1]);
                from = this.toYearMonthDate(monthStart);
                const monthEnd = monthStart.endOf('months').add(1, 'days');
                to = this.toYearMonthDate(monthEnd);

                return {
                    from: from,
                    to: to
                };
            case 'year':
                const yearStart = moment([now.getFullYear(), 0, 1]);
                from = this.toYearMonthDate(yearStart);
                const yearEnd = yearStart.endOf('year').add(1, 'days');
                to = this.toYearMonthDate(yearEnd);

                return {
                    from: from,
                    to: to
                };
            default:
                from = this.toYearMonthDate(moment(now));
                to = this.toYearMonthDate(moment(now).add(1, 'days'));

                return {
                    from: from,
                    to: to,
                };
        }
    }

    toYearMonthDate(momentObj) {
        return `${momentObj.year()}-${momentObj.month() + 1}-${momentObj.date()}`;
    }

    getSimplePhotoDetail(id) {
        return this.http.get(this.photoUrl + '/' + id + '?filter[include]=customer');
    }
    
    countPhotos(params) {
        const where = encodeURIComponent(JSON.stringify({
            ...params
        }));
        
        return this.http.get(this.photoUrl + `/count?where=${where}`);
    }

    getPhotoDetails(id: string, isCount: boolean, userId = null) {
        const url = `${this.photoUrl}/${id}?filter[include]=category&filter[include]=customer&filter[include]=subCategory` +
       `&filter[include]=photoRewards&filter[include]=group&filter[userId]=${userId}&count=${isCount}`;
        return this.http.get(url).toPromise().then(
            response => {
                if (response['IsSuccess']) {
                    const photo = response['Data'];
                    if (!photo.exif) {
                        photo.exif = {
                            model: 'Canon EOS 1D X Mark II',
                            lens: '50mm F1.8',
                            focalLength: '24',
                            aperture: '2.2',
                            ISO: '300',
                            shutterSpeed: '1/1600'
                        };
                    }

                    const partner = GlobalVariables.partnerNavigators.filter(obj => {
                        return obj.id === photo.partnerId;
                    });

                    if (partner.length) {
                        photo.partner = partner[0];
                    }

                    if (photo.groupId) {
                        photo.partner = {
                            id : photo.partnerId
                        }
                    }

                    response['Data'] = photo;

                    return Promise.resolve(response);
                }
            },
            error => {
                console.log('ERROR GET PHOTO DETAIL ' + error);
                console.log(error);
            }
        );
    }

    getPhotosByUserId(userId, photoId) {
        const filter = encodeURIComponent(JSON.stringify({
            include : ['category', 'customer'],
            order : 'create_at DESC',
            where : {
                customerId : userId,
                id : {
                    neq : photoId
                },
                partnerId : {
                    neq : 0
                },
                groupId : null
            },
        }));
        const url = `${this.photoUrl}?filter=${filter}`;

        // return this.photoUrl;
        return this.http.get(url);
    }

    getPrimaryPhotoLocation(locationId) {
        const url = `${this.photoUrl}?filter[where][locationId]=${locationId}&filter[where][isPrimaryLocation]=1`;
        return this.http.get(url);
    }

    getSuggestPhotos(photoId, userId = null) {
        const url = `${this.photoUrl}/findSimilar/${photoId}?filter[include]=category&filter[include]=customer&filter[userId]=${userId}&filter[limit]=10&filter[skip]=0`;

        return this.http.get(url);
    }

    searchPhotos(keyword: string, userId = null) {
        const url = `${this.photoUrl}/search/${keyword}?filter[include]=customer&filter[userId]=${userId}`;

        return this.http.get(url);
    }

    getTags(inputTags) {
        const tags = [];
        if (!inputTags) {
            inputTags = '';
        }
        inputTags.split(',').map(item => {
            if (item !== '') {
                tags.push(item);
            }
        });

        return tags;
    }

    deletePhotoById(photoId) {
        const url = `${this.photoUrl}/${photoId}`;

        return this.http.delete(url);
    }

    openLightbox(image): void {
        this.lightbox.open(
            image,
            0);
    }

    checkGoldenPhoto(id, params) {
        return this.http.get(`${this.photoUrl}/markGoldenPhoto/${id}?filter[photoId]=${params.photoId}`);
    }

    setCachedPhoto(photos) {
        this.cachedPhotos = photos;
    }

    getCachedPhoto() {
        return this.cachedPhotos;
    }

    convertPartnerString(str: string): string {
        return GlobalVariables.CURRENT_PARTNER.value + str;
    }
    
    private getGoldenPhotos (queryObject) {
        return from(this.handeGetGoldenPhoto(queryObject));
    }
    
    private async handeGetGoldenPhoto(queryObject) {
        if (!PhotoService.GOLDEN_PHOTO_IDS.length) {
            const filter = encodeURIComponent(JSON.stringify(queryObject));
            const url = `${this.photoIdsByCriteria}?filter=${filter}`;
            const responseData: any = await this.http.get(url).toPromise();
            PhotoService.GOLDEN_PHOTO_IDS = responseData.Data || [];
        }
        
        if (queryObject.skip == 0) {
            PhotoService.GOLDEN_PHOTO_IDS = this.shuffleArray(PhotoService.GOLDEN_PHOTO_IDS);
        }
        
        const itemsForPage = this.paginateArray(PhotoService.GOLDEN_PHOTO_IDS, queryObject.skip, queryObject.limit);
        const filter = encodeURIComponent(JSON.stringify({
            where : {
                id : {
                    inq: itemsForPage
                }
            },
            include: 'customer',
        }));
        const getPhotoByIdUrl = `${this.photoUrl}?filter=${filter}`;
        const responsePhoto: any = await this.http.get(getPhotoByIdUrl).toPromise();
        const responsePhotoData: any = responsePhoto.Data;
    
        responsePhoto.Data = responsePhotoData.sort((a, b) => {
            const idA = itemsForPage.indexOf(a.id);
            const idB = itemsForPage.indexOf(b.id);
            
            return idA - idB;
        });
        
        return responsePhoto;
    }
    
    private shuffleArray(arr) {
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }
    
    private paginateArray(array, startIndex, endIndex) {
        // Use slice to extract the items for the current page
        return array.slice(startIndex, startIndex + endIndex);
    }
}
